import { useState } from "react";
import { getUserById } from "../lib/mockData";
import BlurredMessage from "./BlurredMessage";
import { MapPin, Clock, Flag, Ban, Trash2, CheckCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useT } from "../lib/useLanguage";
import { toast } from "sonner";
import { motion } from "framer-motion";

const STATUS_STYLES = {
  pending: { bg: "bg-amber-100", text: "text-amber-700" },
  revealed: { bg: "bg-blue-100", text: "text-blue-700" },
  accepted: { bg: "bg-green-100", text: "text-green-700" },
  declined: { bg: "bg-red-100", text: "text-red-700" },
  blocked: { bg: "bg-gray-100", text: "text-gray-700" },
};

export default function MessageRequestCard({ conv, isReceived }) {
  const t = useT();
  const [status, setStatus] = useState(conv.status);
  const user = getUserById(isReceived ? conv.fromUserId : conv.toUserId);

  function act(action) {
    setStatus(action);
    const msgs = { accepted: "Demande acceptée !", declined: "Refusée.", blocked: "Utilisateur bloqué.", reported: "Signalement envoyé." };
    toast.success(msgs[action] || "OK");
  }

  const ss = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const statusLabel = t(`msg_status_${status}`) || status;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-2xl border border-border p-4 mb-3">
      <div className="flex items-center gap-3 mb-3">
        <img src={user?.photo} alt="" className="h-10 w-10 rounded-xl object-cover border-2 border-border" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm text-foreground">{user?.firstName}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ss.bg} ${ss.text}`}>{statusLabel}</span>
          </div>
          <p className="text-xs text-muted-foreground truncate">{user?.ageRange} · {user?.status}</p>
        </div>
        <span className="text-xs text-muted-foreground shrink-0">
          {new Date(conv.createdAt).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
        </span>
      </div>

      <div className="mb-2">
        <p className="font-semibold text-sm text-foreground mb-1">{conv.skill}</p>
        <p className="text-xs text-muted-foreground">{conv.goal}</p>
        <div className="flex gap-3 mt-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{conv.zone}</span>
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{conv.duration}</span>
        </div>
      </div>

      {isReceived && (status === "pending" || status === "revealed") ? (
        <div className="mt-3">
          <BlurredMessage
            message={conv.message}
            onReveal={() => act("revealed")}
            onDecline={() => act("declined")}
          />
          {status === "revealed" && (
            <div className="flex gap-2 mt-3">
              <Button size="sm" className="flex-1 rounded-full text-xs gap-1" style={{ backgroundColor: "#B9F85A", color: "#101828" }} onClick={() => act("accepted")}>
                <CheckCircle className="h-3.5 w-3.5" />{t("msg_accept")}
              </Button>
              <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => act("declined")}>{t("msg_decline")}</Button>
              <Button size="sm" variant="ghost" className="rounded-full text-xs text-red-500 px-2" onClick={() => act("blocked")}><Ban className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="ghost" className="rounded-full text-xs text-orange-500 px-2" onClick={() => act("reported")}><Flag className="h-3.5 w-3.5" /></Button>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-3 text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2">
          {isReceived
            ? (status === "accepted" ? "Conversation active." : status === "declined" ? "Refusée." : "Bloquée.")
            : (status === "pending" ? t("msg_pending_sent") : status === "accepted" ? "Acceptée ! Tu peux continuer." : "Refusée.")
          }
        </div>
      )}
    </motion.div>
  );
}